import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, of } from 'rxjs';

import {
    AggregationTimeScale,
    CsvAsStringArrays,
    getPlatformKeysWithRating,
    isNotNil,
    MalouMetric,
    PlatformFilterPage,
    StatisticsType,
} from '@malou-io/package-utils';

import { LightRestaurant } from '#modules/restaurants/models/restaurant';
import { AbstractCsvService } from '#modules/statistics/components/download-insights-modal/models/csv-service.abstract';
import { InsightsAggregatedV2Response, InsightsByPlatformByRestaurant } from '#modules/statistics/models/insights';
import { InsightsApiService } from '#modules/statistics/services/insights.api.service';
import { AggregatedStatisticsStore } from '#modules/statistics/stores/aggregated-store/aggregated-statistics.store';
import { CsvNumberFormatter } from '#shared/helpers/number-formatter';
import { EnumTranslatePipe } from '#shared/pipes/enum-translate.pipe';

interface Data {
    platformsRatingsByRestaurant: InsightsByPlatformByRestaurant;
    restaurants: LightRestaurant[];
}

interface Options {
    restaurants: LightRestaurant[];
}

@Injectable()
export class AggregatedPlatformsRatingsCsvInsightsService extends AbstractCsvService<Data, Options> {
    private readonly _enumTranslatePipe = inject(EnumTranslatePipe);
    private readonly _csvNumberFormatter = inject(CsvNumberFormatter);
    private readonly _aggregatedStatisticsStore = inject(AggregatedStatisticsStore);
    private readonly _insightsApiService = inject(InsightsApiService);

    protected override _isDataValid(data: Data): boolean {
        return Object.keys(data.platformsRatingsByRestaurant).length !== 0;
    }

    // The ratings table only loads the last rating of each platform: the daily ratings are fetched for the export only.
    protected override _getData$({ restaurants }: Options): Observable<Data> {
        const { startDate, endDate } = this._aggregatedStatisticsStore.datesFilter();
        const platformKeysWithRating = getPlatformKeysWithRating();
        const platformKeys = this._aggregatedStatisticsStore
            .getPlatformsFiltersByPage({ page: PlatformFilterPage.E_REPUTATION })
            .filter((platformKey) => platformKeysWithRating.includes(platformKey));
        if (!startDate || !endDate || !platformKeys.length) {
            return of({ platformsRatingsByRestaurant: {}, restaurants });
        }

        return this._insightsApiService
            .getInsightsV2({
                restaurantGroupSelection: this._aggregatedStatisticsStore.restaurantGroupSelectionFilter(),
                startDate: startDate.toISOString(),
                endDate: endDate.toISOString(),
                platformKeys,
                metrics: [MalouMetric.PLATFORM_RATING],
                aggregators: [AggregationTimeScale.BY_DAY],
                statisticsType: StatisticsType.E_REPUTATION,
            })
            .pipe(
                map((res) => ({ platformsRatingsByRestaurant: this._getRatingsByRestaurantId(res.data, restaurants), restaurants })),
                // The archive is built from all the selected charts at once: a failed call must only leave this file out.
                catchError(() => of({ platformsRatingsByRestaurant: {}, restaurants }))
            );
    }

    // Brand accounts are left out before the validity check: a selection of brand accounts alone must export no file.
    private _getRatingsByRestaurantId(
        insights: InsightsAggregatedV2Response | null,
        restaurants: LightRestaurant[]
    ): InsightsByPlatformByRestaurant {
        const brandBusinessIds = new Set(restaurants.filter((restaurant) => restaurant.isBrandBusiness()).map(({ id }) => id));
        const restaurantsInsights = [
            ...(insights?.groups.flatMap((group) => group.restaurants) ?? []),
            ...(insights?.ungroupedRestaurants ?? []),
        ].filter(({ restaurantId }) => !brandBusinessIds.has(restaurantId));
        return Object.fromEntries(restaurantsInsights.map((restaurant) => [restaurant.restaurantId, restaurant.insights]));
    }

    protected override _getCsvDataRows({ platformsRatingsByRestaurant, restaurants }: Data): CsvAsStringArrays {
        return Object.entries(platformsRatingsByRestaurant)
            .map(([restaurantId, platformsRatings]) => {
                const restaurant = restaurants.find((r) => r.id === restaurantId);
                if (!restaurant) {
                    return null;
                }
                const restaurantName = restaurant.name;
                const restaurantInternalName = restaurant.internalName ?? '';
                const restaurantAddress = restaurant.getFullFormattedAddress();
                return Object.entries(platformsRatings)
                    .map(
                        ([platformKey, platformRatings]) =>
                            platformRatings[AggregationTimeScale.BY_DAY]?.platform_rating?.map((rating) => {
                                const date = new Date(rating.date).toISOString();
                                const ratingValue = this._csvNumberFormatter.format(rating.value);
                                const platformKeyTranslated = this._enumTranslatePipe.transform(platformKey, 'platform_key');
                                return [
                                    date,
                                    restaurantName,
                                    restaurantInternalName,
                                    restaurantAddress,
                                    ratingValue,
                                    platformKeyTranslated,
                                ];
                            }) ?? []
                    )
                    .flat();
            })
            .filter(isNotNil)
            .flat();
    }

    protected override _getCsvHeaderRow(): CsvAsStringArrays[0] {
        return ['Date', 'Location', 'Location Internal Name', 'Location Address', 'Rating', 'Platform'];
    }
}
